import { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "framer-motion";
import { keyBy, range } from "lodash-es";
import { twJoin } from "tailwind-merge";

import { Flow, FlowVersionStatus } from "src/api/flowTypes";
import { usePublishFlowVersion } from "src/api/flowVersionQueries";
import {
  useActivateTrafficPolicy,
  useFlow,
  useTrafficPolicies,
} from "src/api/queries";
import { useUserById } from "src/api/taktile/queries";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { CustomPopover } from "src/base-components/Popover";
import {
  TrafficPolicyInDB,
  TrafficPolicyWindowInDB,
} from "src/clients/flow-api";
import { AssembleDatasetModal } from "src/datasets/AssembleDatasetModal/Modal";
import { ColorIcon } from "src/design-system/ColorIcon";
import { toastActions } from "src/design-system/Toast/utils";
import { SubHeader } from "src/flow/SubHeader";
import { ExcludesFalse } from "src/flow/types";
import { User } from "src/flow/User";
import { useCapabilities } from "src/hooks/useCapabilities";
import { tableWidth } from "src/layout/constants";
import { ManageTrafficModal } from "src/manageTraffic/Modal";
import { useFlowContext } from "src/router/routerContextHooks";
import { useUserData } from "src/store/AuthStore";
import { formatDate } from "src/utils/datetime";
import { getVersionsColors } from "src/utils/getVersionsColors";
import { logger } from "src/utils/logger";

type TimelinePoint = {
  timeWindow: TrafficPolicyWindowInDB;
  trafficPolicy: TrafficPolicyInDB;
  userId: string;
};

const getTimelineForFlow = (
  flowTrafficPolicies: TrafficPolicyInDB[],
): TimelinePoint[] => {
  const timelinePoints = flowTrafficPolicies.flatMap((trafficPolicy) =>
    trafficPolicy.traffic_policy_windows
      ? trafficPolicy.traffic_policy_windows.map((timeWindow) => ({
          timeWindow,
          trafficPolicy,
          userId: timeWindow.created_by_user_id,
        }))
      : [],
  );

  return timelinePoints.sort(
    (a, b) =>
      new Date(b.timeWindow.starts_at).valueOf() -
      new Date(a.timeWindow.starts_at).valueOf(),
  );
};

const PolicyReactivationModal: React.FC<{
  afterLeave: () => void;
  onConfirm: (policyId: string, archivedVersionIds: string[]) => void;
  onClose: () => void;
  open: boolean;
  flow: Flow;
  rollbackPolicy?: TrafficPolicyInDB;
  isLoading?: boolean;
}> = ({
  rollbackPolicy,
  afterLeave,
  open,
  onClose,
  onConfirm,
  flow,
  isLoading,
}) => {
  const versionsByKey = keyBy(flow.versions, "id");
  const policyArchivedVersions =
    rollbackPolicy?.policy?.flow_versions.filter(
      (version) =>
        versionsByKey[version.flow_version_id]?.status ===
        FlowVersionStatus.ARCHIVED,
    ) ?? [];

  const moreThanOneArchivedVersions = policyArchivedVersions.length > 1;
  const pluralEnding = moreThanOneArchivedVersions ? "s" : "";

  return (
    <ConfirmationModal
      afterLeave={afterLeave}
      confirmationButtonText={
        policyArchivedVersions.length
          ? `Republish version${pluralEnding} and Rollback`
          : "Rollback"
      }
      disableConfirmationButton={isLoading}
      open={open}
      title={rollbackPolicy ? `Rollback to ${rollbackPolicy.name}` : ""}
      onClose={onClose}
      onConfirm={() =>
        rollbackPolicy &&
        onConfirm(
          rollbackPolicy.id,
          policyArchivedVersions.map((v) => v.flow_version_id),
        )
      }
    >
      {rollbackPolicy && (
        <div className="font-inter-normal-13px text-gray-500">
          <p className="mb-2">
            Your current policy will be deactivated, and{" "}
            <strong className="font-inter-semibold-13px text-gray-800">
              {rollbackPolicy.name}
            </strong>{" "}
            will be reactivated with its existing settings. Are you sure you
            want to rollback to{" "}
            <strong className="font-inter-semibold-13px text-gray-800">
              {rollbackPolicy.name}
            </strong>
            ?
          </p>
          {policyArchivedVersions.length > 0 && (
            <>
              <p className="mb-2">
                <strong className="font-inter-semibold-13px text-gray-800">
                  {rollbackPolicy.name}
                </strong>{" "}
                used{" "}
                {moreThanOneArchivedVersions
                  ? policyArchivedVersions?.length
                  : "a"}{" "}
                version
                {moreThanOneArchivedVersions && "s"} which are currently
                archived:
              </p>
              <ul className="mb-2 ml-4 list-disc">
                {policyArchivedVersions?.map((version) => (
                  <li
                    key={version.flow_version_id}
                    className="font-inter-semibold-13px text-gray-800"
                  >
                    {versionsByKey[version.flow_version_id]?.name}
                  </li>
                ))}
              </ul>
              <p className="mb-2">
                Do you want to republish these versions which are currently
                archived?
              </p>
            </>
          )}
        </div>
      )}
    </ConfirmationModal>
  );
};

export const FlowTrafficContent: React.FC = () => {
  const {
    flowVersions: { canSetDefault },
  } = useCapabilities();
  const { flow } = useFlowContext();
  const flowTrafficPolicies = useTrafficPolicies(flow.id);
  const { mutateAsync: activateTrafficPolicy } = useActivateTrafficPolicy();
  const { mutateAsync: publishVersion } = usePublishFlowVersion();

  const [assembleDatasetWithPolicy, setAssembleDatasetWithPolicy] =
    useState<string>();

  const [isTrafficModalOpen, setIsTrafficModalOpen] = useState(false);

  const [rollbackModalPolicy, setRollbackModalPolicy] =
    useState<TrafficPolicyInDB>();
  const [isRollbackModalOpen, setIsRollbackModalOpen] = useState(false);

  // Fetch flow with archived versions so the traffic allocation bars and the
  // rollback modal can resolve archived versions by id.
  const { data: flowWithArchived, isLoading: isLoadingFlowWithArchived } =
    useFlow(flow.id, {
      includeArchivedFlowVersions: true,
    });

  const { signed_in_user_id } = useUserData();

  const onActivateTrafficPolicy = async (
    policyId: string,
    archivedVersionIds: string[],
  ) => {
    const versionsToSearch = flowWithArchived?.versions ?? flow.versions;
    try {
      for (const versionId of archivedVersionIds) {
        const flowVersion = versionsToSearch.find((v) => v.id === versionId);
        if (!flowVersion) continue;
        await publishVersion({
          publishArgs: {
            version: flowVersion,
            name: flowVersion.name,
            release_note: flowVersion.meta.release_note ?? "",
            published_by_id: signed_in_user_id ?? "",
            published_at: new Date(Date.now()).toISOString(),
          },
          enableTrafficPolicies: false,
        });
      }
      await activateTrafficPolicy({
        flowId: flow.id,
        trafficPolicyId: policyId,
      });
    } catch (err) {
      logger.error("Rollback error", err);
      toastActions.failure({
        title: "Rollback failed",
        description:
          "Rollback to a different policy failed due to an error. Please try again later.",
      });
    } finally {
      setIsRollbackModalOpen(false);
    }
  };

  const atLeastTwoVersionsPublished =
    flow.versions.filter((v) => v.status === FlowVersionStatus.PUBLISHED)
      .length >= 2;
  return (
    <>
      <SubHeader title="Routing policies" paddedParent>
        {canSetDefault && (
          <SubHeader.Button
            dataLoc="manage-traffic-button"
            disabled={!atLeastTwoVersionsPublished}
            icon={faPlus}
            tooltip={
              atLeastTwoVersionsPublished
                ? "Manage routing"
                : "Managing traffic allocation requires at least two published versions."
            }
            onClick={() => setIsTrafficModalOpen(true)}
          />
        )}
      </SubHeader>
      <div
        className={twJoin(
          "mx-auto flex w-full min-w-[1000px] flex-col rounded-lg border border-gray-200 bg-white",
          tableWidth,
        )}
      >
        <div
          className="ml-[90px] flex-1 border-gray-100 border-l"
          data-loc="routing-policies-table"
        >
          <div className="flex h-[42px] items-center pl-[180px] font-inter-medium-12px">
            <div className="w-[294px]">Traffic allocation</div>
            <div className="w-[240px]">Activated by</div>
            <div>Activated on</div>
          </div>
          {flowTrafficPolicies.data ? (
            <Timeline
              flow={flowWithArchived ?? flow}
              timeline={getTimelineForFlow(flowTrafficPolicies.data)}
              onActivateTrafficPolicy={(policy) => {
                setIsRollbackModalOpen(true);
                setRollbackModalPolicy(policy);
              }}
              onAssembleDatasetWithTrafficPolicy={setAssembleDatasetWithPolicy}
            />
          ) : (
            <TimelineSkeleton />
          )}
        </div>
        {flowTrafficPolicies.data && (
          <AssembleDatasetModal
            flow={flow}
            open={!!assembleDatasetWithPolicy}
            trafficPolicies={flowTrafficPolicies.data}
            trafficPolicyIdDefaultOverride={assembleDatasetWithPolicy}
            onClose={() => {
              setAssembleDatasetWithPolicy(undefined);
            }}
          />
        )}
        {flow.active_traffic_policy?.policy && (
          <ManageTrafficModal
            afterLeave={() => undefined}
            currentTrafficPolicy={flow.active_traffic_policy.policy}
            flowId={flow.id}
            open={isTrafficModalOpen}
            publishedVersions={flow.versions.filter(
              (version) => version.status === FlowVersionStatus.PUBLISHED,
            )}
            onClose={() => setIsTrafficModalOpen(false)}
          />
        )}
        <PolicyReactivationModal
          afterLeave={() => setRollbackModalPolicy(undefined)}
          flow={flowWithArchived ?? flow}
          isLoading={isLoadingFlowWithArchived}
          open={!!isRollbackModalOpen}
          rollbackPolicy={rollbackModalPolicy}
          onClose={() => setIsRollbackModalOpen(false)}
          onConfirm={onActivateTrafficPolicy}
        />
      </div>
    </>
  );
};

const Timeline: React.FC<{
  timeline: TimelinePoint[];
  onAssembleDatasetWithTrafficPolicy: (policyId: string) => void;
  flow: Flow;
  onActivateTrafficPolicy: (policy: TrafficPolicyInDB) => void;
}> = ({
  timeline,
  onAssembleDatasetWithTrafficPolicy,
  flow,
  onActivateTrafficPolicy,
}) => {
  const {
    flowVersions: { canSetDefault },
  } = useCapabilities();
  return (
    <AnimatePresence initial={false}>
      {timeline.map((timelinePoint) => {
        const isActivePolicy =
          timelinePoint.trafficPolicy.id === flow.active_traffic_policy?.id;
        const isActiveWindow = !timelinePoint.timeWindow.ends_at;
        return (
          <TimelineRow
            key={timelinePoint.timeWindow.id}
            canSetDefault={canSetDefault}
            flow={flow}
            isActivePolicy={isActivePolicy}
            isActiveWindow={isActiveWindow}
            timelinePoint={timelinePoint}
            type="interactive"
            onActivateTrafficPolicy={onActivateTrafficPolicy}
            onAssembleDatasetWithTrafficPolicy={
              onAssembleDatasetWithTrafficPolicy
            }
          />
        );
      })}
    </AnimatePresence>
  );
};
const TimelineSkeleton: React.FC = () => {
  return (
    <AnimatePresence initial={false}>
      {range(7).map((i) => (
        <TimelineRow key={i} i={i} type="skeleton" />
      ))}
    </AnimatePresence>
  );
};

type TimelineRowProps =
  | {
      type: "interactive";
      timelinePoint: TimelinePoint;
      isActiveWindow: boolean;
      flow: Flow;
      isActivePolicy: boolean;
      canSetDefault: boolean;
      onAssembleDatasetWithTrafficPolicy: (policyId: string) => void;
      onActivateTrafficPolicy: (policy: TrafficPolicyInDB) => void;
    }
  | { type: "skeleton"; i: number };

const TimelineRow: React.FC<TimelineRowProps> = (props) => {
  const movingElement = (
    <div
      className="absolute inset-0 -translate-x-full transform bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100"
      style={{ animation: "shimmer 1s infinite" }}
    ></div>
  );
  const userQuery = useUserById(
    props.type === "interactive" ? props.timelinePoint.userId : undefined,
  );

  const getTimelineDate = (props: TimelineRowProps) => {
    if (props.type === "interactive") {
      return props.isActiveWindow
        ? "Present"
        : formatDate(props.timelinePoint.timeWindow.ends_at!, "MMM d");
    }

    return props.i === 0 ? (
      "Present"
    ) : (
      <div className="relative h-4 w-[54px] overflow-hidden rounded bg-gray-100">
        {movingElement}
      </div>
    );
  };

  return (
    <m.div
      animate={{
        opacity: 1,
        transition: {
          type: "tween",
          ease: "easeOut",
          delay: 0.25,
        },
      }}
      className="group relative flex h-[68px] items-center border-gray-100 border-t pr-6 pl-10"
      data-loc={`${props.type}-timeline-row`}
      initial={{ opacity: 0 }}
      transition={{ type: "tween", ease: "easeOut" }}
      layout
    >
      <div className="absolute -top-[1px] left-[7px] flex -translate-x-full -translate-y-1/2 items-center font-inter-normal-12px text-gray-500">
        {getTimelineDate(props)}
        <div className="ml-1">
          <ColorIcon
            color={
              (props.type === "interactive" && props.isActiveWindow) ||
              (props.type === "skeleton" && props.i === 0)
                ? "indigo-500"
                : "gray-400"
            }
            size="3xs"
          />
        </div>
      </div>
      <div className="w-[140px] truncate pr-4 font-inter-medium-12px text-gray-800 leading-[1]">
        {props.type === "interactive" ? (
          props.timelinePoint.trafficPolicy.name
        ) : (
          <div className="relative h-4 w-[112px] overflow-hidden rounded bg-gray-100">
            {movingElement}
          </div>
        )}
      </div>
      <div className="w-[214px] self-stretch">
        {props.type === "interactive" ? (
          <PolicyGraph
            flow={props.flow}
            policy={props.timelinePoint.trafficPolicy}
          />
        ) : (
          <div className="flex h-full w-full items-center">
            <div className="relative h-4 w-full overflow-hidden rounded bg-gray-100">
              {movingElement}
            </div>
          </div>
        )}
      </div>
      <div className="ml-[80px] w-[240px] overflow-hidden pr-2 font-inter-normal-12px text-gray-500">
        {props.type === "interactive" && !userQuery?.isLoading ? (
          userQuery?.data && (
            <User
              avatar={userQuery.data.avatar_url}
              name={userQuery.data.username ?? ""}
            />
          )
        ) : (
          <div className="relative h-4 w-[136px] overflow-hidden rounded bg-gray-100">
            {movingElement}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 flex-grow font-inter-normal-12px text-gray-500 leading-[1]">
        {props.type === "interactive" ? (
          formatDate(props.timelinePoint.timeWindow.starts_at)
        ) : (
          <div className="relative h-4 w-[136px] overflow-hidden rounded bg-gray-100">
            {movingElement}
          </div>
        )}
      </div>
      {props.type === "interactive" && (
        <EllipsisOptionsDropdown
          buttonClassName="opacity-0 group-hover:opacity-100"
          dataLoc="timeline-row-options"
          elements={[
            !props.isActivePolicy &&
              props.canSetDefault && {
                key: "Rollback to this policy",
                action: () => {
                  props.onActivateTrafficPolicy(
                    props.timelinePoint.trafficPolicy,
                  );
                },
              },
            {
              key: "Assemble dataset",
              action: () => {
                props.onAssembleDatasetWithTrafficPolicy(
                  props.timelinePoint.trafficPolicy.id,
                );
              },
            },
          ].filter(Boolean as unknown as ExcludesFalse)}
          ellipsisDirection="vertical"
          iconSize="xs"
          timeoutDuration={0}
        />
      )}
    </m.div>
  );
};

const PolicyGraph: React.FC<{
  policy: TrafficPolicyInDB;
  flow: Flow;
}> = ({ policy, flow }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (!policy.policy) return null;

  const policyVersions = flow.versions.filter(
    (v) =>
      v.status === FlowVersionStatus.PUBLISHED ||
      v.status === FlowVersionStatus.ARCHIVED,
  );
  const colors = getVersionsColors(policyVersions);

  const sortedVersions = policy.policy.flow_versions
    .filter((version) => version.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage);

  return (
    <CustomPopover
      button={
        <div className="flex h-full items-center">
          <div className="flex h-3 w-60 overflow-hidden rounded-sm">
            {sortedVersions.map((version) => (
              <div
                key={version.flow_version_id}
                style={{
                  width: `${version.percentage}%`,
                  backgroundColor:
                    colors[version.flow_version_id] ?? "transparent",
                }}
              ></div>
            ))}
          </div>
        </div>
      }
      dataLoc="policy-graph"
      isOpen={tooltipOpen}
      placement="right"
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
    >
      <div className="z-10 min-w-[160px] max-w-[450px] px-4 py-1">
        {sortedVersions.map((version) => (
          <div
            key={version.flow_version_id}
            className="flex h-11 items-center border-gray-100 border-t font-inter-normal-12px text-gray-500 first:border-0"
          >
            <div
              className="h-2 w-2 rounded-[1px]"
              style={{
                backgroundColor:
                  colors[version.flow_version_id] ?? "transparent",
              }}
            />
            <div className="ml-2 min-w-0 flex-1 truncate pr-2">
              {
                flow.versions.find((v) => v.id === version.flow_version_id)
                  ?.name
              }
            </div>
            <div className="font-inter-semibold-13px text-gray-700">
              {version.percentage}%
            </div>
          </div>
        ))}
      </div>
    </CustomPopover>
  );
};
